// The Google Ads tag, as both sites use it (#754): which parameters carry an
// ad click from page to page, how the tag is started while a grant is in
// force, and which cookies a Decline removes. On the marketing site the same
// tag also carries Google Analytics 4 page views (#783, below).
//
// Google Ads conversion measurement on the web, for a sign-up and for a
// completed Checkout, under the same one answer as analytics — opt-out
// (#755), so on unless the visitor declines or the browser sends Global Privacy
// Control (./consent.js). The runtimes are ./adMeasurement.js on the
// marketing site and src/lib/adConversions.js in the app; this file is what they share, so it
// lives where the consent cookie does and for the same reason (./consent.js
// says why): the marketing bundle may not import from src/, and the app may
// import from here. It imports nothing but ./consent.js, which imports
// nothing.
//
// ── The Google tag, not Tag Manager ───────────────────────────────────────
//
// gtag.js is loaded with the Ads id and told everything it does in code here:
// the consent state, the configuration, and the one event the app sends. A
// Tag Manager container would put what may be sent in a console outside the
// repository, where no test reads it.
//
// ── Its own data layer ────────────────────────────────────────────────────
//
// In the app, Firebase Analytics owns `window.dataLayer` and `window.gtag`
// and wraps them, holding back commands for its own measurement ids until it
// has initialised. The Ads tag is started on a data layer of its own
// (`TAG_LAYER`, gtag.js's documented `l=` parameter) and keeps its `gtag`
// function private, so neither tag waits on the other, either can be
// configured without the other, and a Decline is applied to each by its own
// runtime. (Reasoned from gtag.js's documentation, not observed; Tag
// Assistant shows what the tag does.)
//
// ── Carrying a click without storing it ───────────────────────────────────
//
// An ad click arrives as `?gclid=…` (or `gbraid` / `wbraid` from iOS, and the
// campaign's `utm_*` tags) on whichever marketing page the ad points at, and
// the sign-up it should be credited with happens on the app's own host,
// usually a page or two later. Google's answer is `url_passthrough`, which
// copies those parameters onto the page's links — but it needs gtag.js loaded
// to do it, and a page should carry the click on its first paint, before the
// tag has arrived (or when an ad blocker keeps it away). `withAttribution`
// does the same copying in a few lines of our own, whenever measurement is in
// force: the parameters the visitor's own address
// already carries are added to links to this site and to the app, and to no
// other host. Nothing is written to a cookie or to storage, and nothing is
// sent anywhere; the address bar is where Google put them. Once loaded the
// tag stores the click in its own `_gcl_*` cookie on the brand's domain, which
// both sites share, so a visitor who comes back days later without the
// parameters is still credited.
//
// ── GA4 on the same tag, on the marketing site ────────────────────────────
//
// GA4 page views on the marketing site (#783), carrying traffic sources and
// UTMs per page. The stream is the app's own
// (`VITE_FIREBASE_MEASUREMENT_ID`), so the two sites are one property and
// one client id. It rides on the Google tag above rather than loading
// gtag.js a second time: one gtag.js, one data layer, and the GA4 id is a
// second `config` on it (`startGoogleTag`'s `also`), so the one consent
// state and the one Decline govern both ids. With no Ads id configured the
// tag is loaded with the GA4 id instead. In the app Firebase Analytics loads
// its own gtag.js and none of this applies there.

import { CONSENT_MAX_AGE_SECONDS, CONSENT_MODE, cookieDomainFor } from './consent.js';

/** The ad click ids, in the order they are carried. */
export const CLICK_IDS = Object.freeze(['gclid', 'gbraid', 'wbraid']);
/** The campaign tags, carried with them so the app's address reads as the ad's did. */
export const CAMPAIGN_TAGS = Object.freeze(['utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content']);

// What a value may look like to be carried at all. A click id is Google's own
// opaque token; a campaign tag is written by whoever made the ad, so it is
// held to words, digits and a little punctuation, and anything else — an
// address, a sentence with a question mark — is simply not carried.
const CLICK_ID_VALUE = /^[A-Za-z0-9_-]{1,256}$/;
const CAMPAIGN_VALUE = /^[A-Za-z0-9 _.~:+-]{1,100}$/;

/**
 * The attribution parameters in a query string, as `[key, value]` pairs in
 * the fixed order above. Any other parameter, and any value of the wrong
 * shape, is left out.
 */
export function attributionFrom(search) {
  let params;
  try {
    params = new URLSearchParams(String(search || ''));
  } catch {
    return [];
  }
  const out = [];
  for (const key of CLICK_IDS) {
    const v = params.get(key);
    if (v != null && CLICK_ID_VALUE.test(v)) out.push([key, v]);
  }
  for (const key of CAMPAIGN_TAGS) {
    const v = params.get(key);
    if (v != null && CAMPAIGN_VALUE.test(v)) out.push([key, v]);
  }
  return out;
}

/** Only the click ids of `attributionFrom`'s pairs. */
export const clickIdsOf = (pairs) => pairs.filter(([k]) => CLICK_IDS.includes(k));

/** `pairs` as a query string, `?a=1&b=2`, or '' when there are none. */
export function queryOf(pairs) {
  if (!pairs.length) return '';
  return `?${new URLSearchParams(pairs).toString()}`;
}

/**
 * `href` with `pairs` added, when it is a link to one of `hosts` — this site,
 * or the app's — and otherwise exactly as given: a link to anybody else's
 * site never carries a click id. A parameter the link already has is not
 * replaced. A same-origin link stays a path, so `/features.html` becomes
 * `/features.html?gclid=…` rather than an absolute URL.
 * @param {string} href the link as written
 * @param {Array<[string, string]>} pairs from `attributionFrom`
 * @param {{ base: string, hosts: string[] }} where
 */
export function withAttribution(href, pairs, { base, hosts }) {
  if (!pairs.length || typeof href !== 'string' || !href || href.startsWith('#')) return href;
  let url;
  let from;
  try {
    url = new URL(href, base);
    from = new URL(base);
  } catch {
    return href;
  }
  if (!/^https?:$/.test(url.protocol) || !hosts.includes(url.host)) return href;
  let changed = false;
  for (const [k, v] of pairs) {
    if (url.searchParams.has(k)) continue;
    url.searchParams.append(k, v);
    changed = true;
  }
  if (!changed) return href;
  const written = /^[a-z][a-z0-9+.-]*:|^\/\//i.test(href);
  return !written && url.origin === from.origin ? `${url.pathname}${url.search}${url.hash}` : url.href;
}

// Each decorated link's own href, so a Decline can put it back.
const originals = new WeakMap();

/** Add `pairs` to every link in `doc` that `withAttribution` would change. */
export function carryAttribution(doc, pairs, hosts) {
  if (!pairs.length) return 0;
  let n = 0;
  for (const a of doc.querySelectorAll('a[href]')) {
    const own = originals.has(a) ? originals.get(a) : a.getAttribute('href');
    const next = withAttribution(own, pairs, { base: doc.baseURI, hosts });
    if (next === own) continue;
    originals.set(a, own);
    a.setAttribute('href', next);
    n += 1;
  }
  return n;
}

/** Put back every link `carryAttribution` changed. */
export function dropAttribution(doc) {
  for (const a of doc.querySelectorAll('a[href]')) {
    if (!originals.has(a)) continue;
    a.setAttribute('href', originals.get(a));
    originals.delete(a);
  }
}

// ── Starting the tag ──────────────────────────────────────────────────────

export const GOOGLE_TAG_URL = 'https://www.googletagmanager.com/gtag/js';
export const TAG_LAYER = 'codaAdsLayer';

/** A Google Ads tag id: `AW-` and digits. Anything else is not configured. */
export const isAdsId = (id) => typeof id === 'string' && /^AW-\d{6,15}$/.test(id);

/**
 * The configuration the Ads tag is given, on either site.
 *
 * No page view: on an `AW-` id a page view is a remarketing hit, and this is
 * measurement, not remarketing — `allow_ad_personalization_signals` off says
 * the same again. No Google signals. The location is the
 * caller's clean one (a route pattern in the app, the page's path on the
 * marketing site) with the click ids and nothing else, so a hit carries what
 * attribution needs and not the real address. The `_gcl_*` cookies go on the
 * brand's domain, where both sites read them, and on the host alone anywhere
 * else.
 *
 * Enhanced conversions are on (#754), with Google's automatic management: while it runs, the tag may find an
 * email address typed or shown on the page — the sign-up form, the
 * check-your-email screen — and send Google Ads a SHA-256 hash of it, to
 * match the conversion to the ad click. Nothing here supplies or strips that
 * data; whether the tag looks for it is the Ads account's setting, and
 * `allow_enhanced_conversions` only keeps this configuration from refusing
 * it. docs/operations.md §5a has the detail.
 * @param {{ pageLocation: string, referrer: string, hostname: string, domain: string|null }} where
 */
export function adTagConfig({ pageLocation, referrer, hostname, domain }) {
  const scope = cookieDomainFor(hostname, domain);
  return {
    send_page_view: false,
    allow_google_signals: false,
    allow_ad_personalization_signals: false,
    allow_enhanced_conversions: true,
    cookie_domain: scope ? scope.slice(1) : 'auto',
    page_location: pageLocation,
    page_referrer: referrerOrigin(referrer),
  };
}

/** A GA4 measurement id: `G-` and letters or digits. Anything else is not configured. */
export const isGaMeasurementId = (id) => typeof id === 'string' && /^G-[A-Z0-9]{4,20}$/.test(id);

/**
 * The configuration GA4 is given on the marketing site (#783).
 *
 * One standard page view, sent by this `config` itself (`send_page_view`,
 * GA4's default, written out so nobody has to know it), with the page's
 * whole address, `location.href`, which is what gtag.js would take by itself:
 * these pages are public and their addresses name nobody, and the `utm_*`
 * tags in it are how GA4 credits a campaign — Meta's `utm_source=meta` among
 * them. It is written out because the Ads id's `config` on the same tag
 * carries a clean `page_location` of its own, and this one must not depend
 * on that staying scoped to the Ads id. The referrer is cut to its origin, the app's
 * rule (`referrerOrigin`), which is all GA4's source classification reads.
 * No Google signals and no ad personalisation, as on the Ads tag. The `_ga`
 * cookies go on the brand's domain, so `app.<domain>` reads the same client
 * id — Firebase Analytics there leaves `cookie_domain` at `'auto'`, which
 * writes on that same parent domain (src/lib/analytics.js) — and last a year,
 * as the app's do and as the answer that allowed them does.
 * @param {{ pageLocation: string, referrer: string, hostname: string, domain: string|null }} where
 */
export function gaTagConfig({ pageLocation, referrer, hostname, domain }) {
  const scope = cookieDomainFor(hostname, domain);
  return {
    send_page_view: true,
    allow_google_signals: false,
    allow_ad_personalization_signals: false,
    cookie_domain: scope ? scope.slice(1) : 'auto',
    cookie_expires: CONSENT_MAX_AGE_SECONDS,
    page_location: pageLocation,
    page_referrer: referrerOrigin(referrer),
  };
}

/**
 * gtag.js's documented per-id switch: while `window['ga-disable-<id>']` is
 * true the GA4 id sends nothing more from this page, its automatic
 * engagement hit on leaving included. Consent Mode's denial alone would
 * still let the tag send cookieless pings (rule 4 in docs/operations.md §5a
 * says why that is not chosen). (Reasoned from Google's documentation, not
 * observed.)
 */
export function setGaDisabled(win, id, disabled) {
  if (isGaMeasurementId(id)) win[`ga-disable-${id}`] = Boolean(disabled);
}

/**
 * Load gtag.js for `id` on its own data layer, with the consent state of a
 * grant as its default — it is only ever started while one is in force,
 * whether the visitor said OK or has not answered (#755) — then `config`,
 * and then each further `[id, config]` of `also` on the same tag (the GA4
 * stream, #783), so gtag.js is fetched once whatever is configured.
 * Returns the tag's `gtag` function. Nothing is fetched or set until this is
 * called.
 */
export function startGoogleTag({ id, config, also = [], win = window, doc = document }) {
  const layer = Array.isArray(win[TAG_LAYER]) ? win[TAG_LAYER] : [];
  win[TAG_LAYER] = layer;
  // gtag.js reads `arguments` objects off the layer, not arrays.
  function gtag() {
    layer.push(arguments);
  }
  gtag('consent', 'default', { ...CONSENT_MODE.granted });
  gtag('js', new Date());
  gtag('config', id, config);
  for (const [otherId, otherConfig] of also) gtag('config', otherId, otherConfig);
  const script = doc.createElement('script');
  script.async = true;
  script.src = `${GOOGLE_TAG_URL}?id=${encodeURIComponent(id)}&l=${TAG_LAYER}`;
  doc.head.appendChild(script);
  return gtag;
}

/** A started tag, told the answer changed in this page. */
export function updateGoogleTag(gtag, choice) {
  gtag('consent', 'update', { ...CONSENT_MODE[choice === 'granted' ? 'granted' : 'denied'] });
  // Anything still sent after a Decline goes without its ad identifiers.
  gtag('set', 'ads_data_redaction', choice !== 'granted');
}

/** Where a page came from, as an origin only (the app's analytics rule too). */
export function referrerOrigin(referrer) {
  const m = /^(https?:\/\/[^/?#\s]+)/.exec(String(referrer || ''));
  return m ? m[1] : '';
}

// ── Withdrawing: the cookies and keys to remove ───────────────────────────

// The conversion linker's `_gcl_aw` (an ad click), `_gcl_gb` / `_gcl_ag`
// (the iOS forms), `_gcl_au` (the tag's own first-party id), `_gcl_dc` and
// their kin, and GA's `_gac_<property>` copy of a click. Any `_gcl_` name is
// removed rather than a list of today's, since Google adds them.
const AD_COOKIE = /^(_gcl_[A-Za-z0-9_]+|_gac_[A-Za-z0-9_]+)$/;

/** The names of the Google Ads cookies in a `document.cookie` string. */
export function adCookieNames(cookieHeader) {
  const names = new Set();
  for (const part of String(cookieHeader || '').split(';')) {
    const eq = part.indexOf('=');
    const name = (eq === -1 ? part : part.slice(0, eq)).trim();
    if (AD_COOKIE.test(name)) names.add(name);
  }
  return [...names];
}

/**
 * Every `document.cookie` assignment that removes the cookies `names` on
 * `hostname`: host-only and on every parent domain of the host. A tag's
 * `cookie_domain: 'auto'` writes on the widest domain the browser accepts,
 * and a cookie can only be removed with the Domain it was set with, so each
 * is tried; the browser ignores the ones it would not have accepted.
 */
export function cookieDeletions(names, hostname) {
  if (!names.length) return [];
  const host = String(hostname || '').toLowerCase().replace(/\.$/, '');
  const labels = host.split('.');
  const domains = [null];
  const isAddress = /^[\d.]+$/.test(host) || host.includes(':');
  if (!isAddress) for (let i = 0; i < labels.length - 1; i += 1) domains.push(`.${labels.slice(i).join('.')}`);
  const out = [];
  for (const name of names) {
    for (const d of domains) out.push(`${name}=; Max-Age=0; Path=/${d ? `; Domain=${d}` : ''}`);
  }
  return out;
}

/** The assignments that remove the Google Ads cookies present. */
export const adCookieDeletions = (cookieHeader, hostname) => cookieDeletions(adCookieNames(cookieHeader), hostname);

// GA's own cookies — `_ga`, `_ga_<stream>`, `_gid`, `_gat*` — which the app's
// Firebase Analytics sets with `cookie_domain: 'auto'`, and the marketing
// site's GA4 (#783) with the brand's domain, so on that domain either way,
// where both sites see them. The app's analytics removes them itself
// (src/lib/analytics.js); the marketing site removes them on a Decline made
// there (#755), since that Decline covers the app as well.
const GA_COOKIE = /^(_ga|_ga_[A-Za-z0-9]+|_gid|_gat(_[A-Za-z0-9_-]+)?)$/;

/** The names of the GA cookies in a `document.cookie` string. */
export function gaCookieNames(cookieHeader) {
  const names = new Set();
  for (const part of String(cookieHeader || '').split(';')) {
    const eq = part.indexOf('=');
    const name = (eq === -1 ? part : part.slice(0, eq)).trim();
    if (GA_COOKIE.test(name)) names.add(name);
  }
  return [...names];
}

/**
 * Remove the Ads cookies from `doc` — and GA's as well when `analytics` is
 * set — and the `_gcl_*` keys gtag.js may keep in localStorage beside them.
 * Returns whether there was anything to remove. Never throws: storage that
 * is blocked held nothing.
 */
export function clearAdStorage({ doc = document, win = window, analytics = false } = {}) {
  let found = false;
  try {
    const names = [...adCookieNames(doc.cookie), ...(analytics ? gaCookieNames(doc.cookie) : [])];
    const deletions = cookieDeletions(names, win.location?.hostname);
    for (const c of deletions) doc.cookie = c;
    found = deletions.length > 0;
  } catch {
    // No cookie jar: nothing to remove.
  }
  try {
    const store = win.localStorage;
    const keys = [];
    for (let i = 0; i < store.length; i += 1) {
      const k = store.key(i);
      if (k && k.startsWith('_gcl_')) keys.push(k);
    }
    for (const k of keys) store.removeItem(k);
    found = found || keys.length > 0;
  } catch {
    // Storage blocked.
  }
  return found;
}
